import React, { useState, useEffect } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Loader2, Plus, Trash2, ArrowRight, Info } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { API_BASE as API } from '../../config/api';

const AllophonesPage = () => {
  const { conlang, canEdit } = useOutletContext();
  const { conlangId } = useParams();
  const [allophones, setAllophones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [phoneme, setPhoneme] = useState('');
  const [allophone, setAllophone] = useState('');
  const [environment, setEnvironment] = useState('');
  const [rule, setRule] = useState('');

  useEffect(() => { fetchData(); }, [conlangId]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/conlangs/${conlangId}/allophones`, { withCredentials: true });
      setAllophones(res.data);
    } catch { toast.error('Failed to load allophones'); }
    finally { setLoading(false); }
  };

  const resetForm = () => { setPhoneme(''); setAllophone(''); setEnvironment(''); setRule(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const res = await axios.post(`${API}/conlangs/${conlangId}/allophones`, {
        phoneme, allophone, environment, rule: rule || null
      }, { withCredentials: true });
      setAllophones([...allophones, res.data]);
      setDialogOpen(false);
      resetForm();
      toast.success('Allophone added');
    } catch { toast.error('Failed to add allophone'); }
    finally { setFormLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this allophone rule?')) return;
    try {
      await axios.delete(`${API}/conlangs/${conlangId}/allophones/${id}`, { withCredentials: true });
      setAllophones(allophones.filter(a => a.allophone_id !== id));
      toast.success('Allophone deleted');
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>;

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-foreground" data-testid="allophones-title">Allophones</h2>
          <p className="text-foreground-muted mt-1">Define allophonic variations in {conlang?.name}</p>
        </div>
        {canEdit && (
        <Button onClick={() => setDialogOpen(true)} className="bg-primary hover:bg-primary/90" data-testid="add-allophone-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Allophone
        </Button>
        )}
      </div>

      {allophones.length > 0 ? (
        <div className="space-y-3" data-testid="allophones-list">
          {allophones.map(a => (
            <Card key={a.allophone_id} className="bg-background-paper border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 font-conlang text-lg">
                      <span className="text-primary font-semibold">/{a.phoneme}/</span>
                      <ArrowRight className="w-4 h-4 text-foreground-muted" />
                      <span className="text-secondary font-semibold">[{a.allophone}]</span>
                    </div>
                    <span className="text-foreground-muted text-sm">/ {a.environment}</span>
                  </div>
                  {canEdit && (
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(a.allophone_id)} className="text-destructive hover:text-destructive" data-testid={`delete-allophone-${a.allophone_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  )}
                </div>
                {a.rule && <p className="text-xs text-foreground-subtle mt-2 font-mono">{a.rule}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bg-background-paper border-border border-dashed">
          <CardContent className="p-12 text-center">
            <h3 className="font-heading text-xl font-semibold text-foreground mb-2">No allophones defined</h3>
            <p className="text-foreground-muted mb-4">Describe how phonemes vary in different environments.</p>
            {canEdit && <Button onClick={() => setDialogOpen(true)} className="bg-primary hover:bg-primary/90"><Plus className="w-4 h-4 mr-2" /> Add Allophone</Button>}
          </CardContent>
        </Card>
      )}

      <div className="p-4 rounded-md bg-primary/10 border border-primary/30 flex gap-3">
        <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
        <div className="text-primary text-sm">
          <strong>Allophones</strong> are variant pronunciations of a phoneme that occur in specific environments. For example, in English, /t/ becomes [tʰ] word-initially (aspirated) but [t] after /s/.
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-background-paper border-border max-w-lg" data-testid="allophone-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">Add Allophone</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phoneme</Label>
                <Input value={phoneme} onChange={(e) => setPhoneme(e.target.value)} placeholder="e.g., t" className="bg-background border-border font-conlang text-lg" required data-testid="allophone-phoneme" />
              </div>
              <div className="space-y-2">
                <Label>Allophone</Label>
                <Input value={allophone} onChange={(e) => setAllophone(e.target.value)} placeholder="e.g., tʰ" className="bg-background border-border font-conlang text-lg" required data-testid="allophone-allophone" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Environment</Label>
              <Input value={environment} onChange={(e) => setEnvironment(e.target.value)} placeholder="e.g., word-initially, before vowels" className="bg-background border-border" required data-testid="allophone-environment" />
            </div>
            <div className="space-y-2">
              <Label>Formal Rule (optional)</Label>
              <Input value={rule} onChange={(e) => setRule(e.target.value)} placeholder="e.g., /t/ → [tʰ] / #_V" className="bg-background border-border font-mono" data-testid="allophone-rule" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</Button>
              <Button type="submit" disabled={formLoading || !phoneme || !allophone || !environment} data-testid="submit-allophone">
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AllophonesPage;
